import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Switch,
  Spinner,
  Chip,
} from '@nextui-org/react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiKeyService, ModelUsageData } from '../../services/apiKeyService';
import { DateRange } from './DateRangeSelector';

interface ModelDistributionChartProps {
  className?: string;
  dateRange?: DateRange;
  apiKeyId?: string;
}

const ModelDistributionChart: React.FC<ModelDistributionChartProps> = ({
  className = '',
  dateRange,
  apiKeyId,
}) => {
  const [modelData, setModelData] = useState<ModelUsageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAsPie, setShowAsPie] = useState(true);

  const loadModelData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use date range if provided, otherwise use default
      const response = dateRange?.isAllTime
        ? await apiKeyService.getModelUsage(
            30,
            undefined,
            undefined,
            true,
            apiKeyId
          ) // All time with proper flag
        : dateRange?.startDate && dateRange?.endDate && !dateRange.isAllTime
          ? await apiKeyService.getModelUsage(
              30,
              dateRange.startDate,
              dateRange.endDate,
              false,
              apiKeyId
            )
          : await apiKeyService.getModelUsage(
              30,
              undefined,
              undefined,
              false,
              apiKeyId
            );
      // Ensure all numeric values are properly processed
      const processedData = (response.models || []).map((item) => ({
        ...item,
        requests: Number(item.requests) || 0,
        avg_response_time: Number(item.avg_response_time) || 0,
        total_tokens: Number(item.total_tokens) || 0,
        error_rate: Number(item.error_rate) || 0,
      }));
      setModelData(processedData);
    } catch (err) {
      console.error('Failed to load model usage data:', err);
      setError('Failed to load model usage data');
      setModelData([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange, apiKeyId]);

  useEffect(() => {
    loadModelData();
  }, [loadModelData]);

  // Format model names for better display
  const formatModelName = (model: string) => {
    if (model.startsWith('ft:gpt-4o')) {
      return 'GPT-4o (Fine-tuned)';
    }
    if (model.startsWith('ft:gpt-4.1')) {
      return 'GPT-4.1 (Fine-tuned)';
    }
    if (model.startsWith('gpt-4o-mini')) {
      return 'GPT-4o Mini';
    }
    if (model.startsWith('gpt-4o')) {
      return 'GPT-4o';
    }
    if (model.includes('bedrock')) {
      return 'Bedrock';
    }
    if (model === 'Unknown') {
      return 'Unknown Model';
    }
    return model.length > 20 ? model.substring(0, 17) + '...' : model;
  };

  // Prepare chart data
  const getChartData = () => {
    return modelData.map((item) => ({
      ...item,
      displayName: formatModelName(item.model),
      fullName: item.model,
      requests: Number(item.requests) || 0,
      avg_response_time: Number(item.avg_response_time) || 0,
      total_tokens: Number(item.total_tokens) || 0,
      error_rate: Number(item.error_rate) || 0,
    }));
  };

  // Colors for pie chart
  const COLORS = [
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff7300',
    '#00ff00',
    '#ff0000',
    '#00ffff',
    '#ff00ff',
  ];

  const renderPieChart = () => {
    const data = getChartData();

    if (!data.length) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <p className="text-sm font-medium mb-2">
              No model usage data available
            </p>
            <p className="text-xs text-gray-400">
              Data will appear once you start making API calls
            </p>
          </div>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={(props) => {
              const { payload, percent } = props;
              return `${payload.displayName}: ${payload.requests} (${((percent || 0) * 100).toFixed(1)}%)`;
            }}
            outerRadius={80}
            fill="#8884d8"
            dataKey="requests"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>

          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '6px',
              fontSize: '12px',
            }}
            formatter={(value: any, name?: string | number) => [
              `${value.toLocaleString()} requests`,
              'Usage',
            ]}
            labelFormatter={(label) => `Model: ${label}`}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const renderBarChart = () => {
    const data = getChartData();

    if (!data.length) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <p className="text-sm font-medium mb-2">
              No model usage data available
            </p>
            <p className="text-xs text-gray-400">
              Data will appear once you start making API calls
            </p>
          </div>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="displayName"
            tick={{ fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '6px',
              fontSize: '12px',
            }}
            formatter={(value: any, name?: string | number) => {
              if (name === 'requests')
                return [`${value.toLocaleString()} requests`, 'Requests'];
              if (name === 'avg_response_time')
                return [`${value.toFixed(2)}ms`, 'Avg Response Time'];
              if (name === 'total_tokens')
                return [`${value.toLocaleString()} tokens`, 'Total Tokens'];
              if (name === 'error_rate')
                return [`${value.toFixed(2)}%`, 'Error Rate'];
              return [value, name];
            }}
            labelFormatter={(label) => `Model: ${label}`}
          />
          <Legend />
          <Bar dataKey="requests" fill="#8884d8" name="Requests" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const getModelStatusColor = (errorRate: number) => {
    if (errorRate === 0) return 'success';
    if (errorRate < 5) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <h3 className="text-lg font-semibold">AI Model Usage</h3>
        </CardHeader>
        <CardBody className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex justify-between items-center w-full">
          <h3 className="text-lg font-semibold">AI Model Usage</h3>
          <div className="flex items-center gap-2">
            <Switch
              size="sm"
              isSelected={showAsPie}
              onValueChange={setShowAsPie}
            />
            <span className="text-sm text-gray-600">Pie Chart</span>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {error ? (
          <div className="text-center text-red-500 py-8">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {showAsPie ? renderPieChart() : renderBarChart()}

            {modelData.length > 0 && (
              <div className="mt-6 space-y-3">
                <h4 className="font-medium text-sm text-gray-700">
                  Model Performance Details
                </h4>
                <div className="grid gap-3">
                  {modelData.map((model, index) => (
                    <div
                      key={model.model}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: COLORS[index % COLORS.length],
                          }}
                        />
                        <div>
                          <p className="font-medium text-sm">
                            {formatModelName(model.model)}
                          </p>
                          <p
                            className="text-xs text-gray-500"
                            title={model.model}
                          >
                            {(model.requests || 0).toLocaleString()} requests
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Response Time</p>
                          <p className="text-sm font-medium">
                            {(model.avg_response_time || 0).toFixed(0)}ms
                          </p>
                        </div>

                        <div className="text-right">
                          <p className="text-xs text-gray-500">Tokens</p>
                          <p className="text-sm font-medium">
                            {(model.total_tokens || 0).toLocaleString()}
                          </p>
                        </div>

                        <Chip
                          color={getModelStatusColor(model.error_rate)}
                          size="sm"
                          variant="flat"
                        >
                          {(model.error_rate || 0).toFixed(1)}% errors
                        </Chip>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-gray-500">Total Models</p>
                    <p className="font-semibold">{modelData.length}</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-gray-500">Most Used</p>
                    <p className="font-semibold">
                      {modelData.length > 0
                        ? formatModelName(modelData[0].model)
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-gray-500">Avg Performance</p>
                    <p className="font-semibold">
                      {modelData.length > 0
                        ? `${(
                            modelData.reduce(
                              (sum, m) => sum + m.avg_response_time,
                              0
                            ) / modelData.length
                          ).toFixed(0)}ms`
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default ModelDistributionChart;
